import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAdminToken } from '@/components/feature/AdminGuard';

const navItems = [
  { icon: 'ri-home-4-line', label: '首页', path: '/admin' },
  { icon: 'ri-shopping-bag-3-line', label: '商品管理', path: '/admin/products' },
  { icon: 'ri-file-list-3-line', label: '订单管理', path: '/admin/orders' },
  { icon: 'ri-user-3-line', label: '会员管理', path: '/admin/members' },
  { icon: 'ri-paint-brush-line', label: '首页装修', path: '/admin/home-design' },
  { icon: 'ri-bar-chart-2-line', label: '数据统计', path: '/admin/stats' },
  { icon: 'ri-history-line', label: '管理日志', path: '/admin/logs' },
  { icon: 'ri-megaphone-line', label: '公告管理', path: '/admin/announcements' },
  { icon: 'ri-feedback-line', label: '用户反馈', path: '/admin/feedbacks' },
  { icon: 'ri-settings-3-line', label: '系统设置', path: '/admin/settings' },
];

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const AdminLayout = ({ children, title }: AdminLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`flex-shrink-0 bg-gray-900 flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-52'}`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-700/50">
          <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-gradient-to-br from-rose-500 to-orange-400 flex items-center justify-center">
            <i className="ri-store-2-line text-white text-sm" />
          </div>
          {!collapsed && (
            <span className="text-white font-bold text-sm whitespace-nowrap">商城后台</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all whitespace-nowrap ${
                  active
                    ? 'bg-rose-500 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <div className="px-2 pb-4">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-gray-500 hover:bg-gray-800 hover:text-white cursor-pointer transition-all"
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-base`} />
            </div>
            {!collapsed && <span className="text-sm whitespace-nowrap">收起菜单</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 flex items-center justify-between px-6 h-14 flex-shrink-0">
          <h1 className="font-semibold text-gray-800 text-base">{title ?? '后台管理'}</h1>
          <div className="flex items-center gap-4">
            <button className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer relative">
              <i className="ri-notification-3-line text-lg" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />
            </button>
            <div className="relative" ref={userMenuRef}>
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => setUserMenuOpen((v) => !v)}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center">
                  <i className="ri-user-line text-white text-sm" />
                </div>
                <span className="text-sm text-gray-700 font-medium">管理员</span>
                <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </div>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg border border-gray-100 overflow-hidden z-50" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-400">当前账号</p>
                    <p className="text-sm font-medium text-gray-700 mt-0.5">管理员</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-rose-500 hover:bg-rose-50 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-logout-box-r-line text-base" />
                    </div>
                    退出登录
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
